import { cacheKeys, useWorkspaceAsset } from '@gripp/shared-logic';
import {
  RoutineButtons,
  RoutineExecutionProps,
  SlideAction,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutinesAssignedListModal } from '../../routines/components/routinesAssignedListModal';
import { useAssetRouting } from '../assetRouteContext';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

const INITIAL_ROUTINES_TO_SHOW = 3;

export const AssetRoutineFold: React.FC<{ assetId: string }> = ({
  assetId,
}) => {
  const { workspaceAsset, invalidateQuery: invalidateWorkspaceAsset } =
    useWorkspaceAsset(assetId);
  const [routinesModalOpened, setRoutinesModalOpened] = useState(false);
  const [initialRoutine, setInitialRoutine] = useState<
    RoutineExecutionProps | undefined
  >();
  const queryClient = useQueryClient();
  const invalidateIssues = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
  }, [queryClient]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onSlideChange } = useSlideLayout();
  const { basePath } = useAssetRouting(assetId);
  const location = useLocation();
  const isRoutinesPage = location.pathname.includes('/routines');

  const onRoutineCompleted = useCallback(async () => {
    setRoutinesModalOpened(false);
    setInitialRoutine(undefined);
    await invalidateWorkspaceAsset();
    await invalidateIssues();
  }, [invalidateIssues, invalidateWorkspaceAsset]);

  const onRoutineSelected = useCallback((routine: RoutineExecutionProps) => {
    setInitialRoutine(routine);
    setRoutinesModalOpened(true);
  }, []);
  const { issueId } = useParams();
  const isOnAssetPage = location.pathname.startsWith('/assets/');

  if (!workspaceAsset || (issueId && isOnAssetPage)) return null;

  const handleViewAllRoutines = async () => {
    if (!isRoutinesPage) {
      await onSlideChange(SlideAction.NEXT);
      navigate(`${basePath}/routines`);
    }
  };

  return (
    <>
      <View style={styles.containerHeader}>
        <ViewAllSectionHeader
          title={t('assets.routines')}
          onPress={handleViewAllRoutines}
        />
        <RoutineButtons
          workspaceAsset={workspaceAsset}
          initialRoutinesToShow={INITIAL_ROUTINES_TO_SHOW}
          showAll={false}
          onRoutineSelected={onRoutineSelected}
        />
      </View>
      <RoutinesAssignedListModal
        isOpened={routinesModalOpened}
        onCompleteRoutines={onRoutineCompleted}
        onCancel={() => {
          setRoutinesModalOpened(false);
          setInitialRoutine(undefined);
        }}
        workspaceAsset={workspaceAsset!}
        initialRoutine={initialRoutine}
      />
    </>
  );
};

const styles = StyleSheet.create({
  containerHeader: {
    marginBottom: 10,
    paddingHorizontal: 16,
    paddingTop: 10,
  },
});
